import { HistoryTradeItem } from '@/components/trading-panel/hooks/useGetHistory';
import { useAddress } from '@/store/selector';

import { renderShortAddress } from '@/utils/format-address';
import { formatDisplay } from '@/utils/format-display';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Sheet } from 'react-modal-sheet';
import ShareWindImage from '../../assets/png/share-wind.png';

import { CONSTANT } from '@/store/constants';
import { MiniKit, ResponseEvent } from '@worldcoin/minikit-js';
import html2canvas from 'html2canvas-pro';
import { useEffect, useRef, useState } from 'react';
import LogoApp from '../../assets/png/logo-app.png';
import ShareLossImage from '../../assets/png/share-loss.png';

import { useAuth } from '@/store/auth';
import { useShallow } from 'zustand/shallow';
import { useRefCode } from '../earn/hooks/useRefCode';
import SpinLoading from '../loading/SpinLoading';
type Props = {
    item: HistoryTradeItem;
    isOpen: boolean;
    onClose: () => void;
};

export default function SharePnl({ item, isOpen, onClose }: Props) {
    const { t } = useTranslation();

    const refShare = useRef<HTMLDivElement>(null);

    const address = useAddress();

    const [isSharing, setIsSharing] = useState(false);

    const [profile_picture_url] = useAuth(
        useShallow((state) => [state.profile_picture_url])
    );

    const { data } = useRefCode();

    const handleShare = async () => {
        if (!refShare.current) {
            return;
        }
        setIsSharing(true);
        const el = refShare.current;
        const originalClass = el.className;
        el.className = originalClass.replace(/!rounded-4xl|rounded-4xl/g, '');
        try {
            const canvas = await html2canvas(el, {
                backgroundColor: null,
                scale: 2,
                useCORS: true,
            });
            // 🪄 Chuyển canvas thành blob thay vì base64
            const blob = await new Promise<Blob>((resolve) =>
                canvas.toBlob((b) => resolve(b!), 'image/png')
            );
            el.className = `${originalClass} rounded-4xl`;

            setIsSharing(false);

            // 🧱 Tạo file từ blob (đúng kiểu TypeScript yêu cầu)
            const file = new File([blob], 'invite.png', { type: 'image/png' });
            const navigateApp = `https://worldcoin.org/mini-app/?app_id=${CONSTANT.APP_ID}&path=/`;
            // 📤 Chia sẻ qua MiniKit
            const data1 = await MiniKit.commandsAsync.share({
                text: `${navigateApp}ref/${data?.refCode}`,
                files: [file], // ✅ Đúng type: File[]
            });

            console.log('✅ Share success', data1);
        } catch (error) {
            console.error('❌ Share error:', error);
        }
    };

    return (
        <Sheet isOpen={isOpen} onClose={onClose} detent="content-height">
            <Sheet.Backdrop className="!bg-transparent3" onTap={onClose} />

            <Sheet.Container className="!bg-transparent p-4  mb-2 flex !flex-col justify-end">
                <Sheet.Content className="h-fit !bg-transparent flex !flex-col justify-end z-50">
                    <div className="bg-[#141414] p-4 rounded-4xl">
                        <div
                            ref={refShare}
                            className="flex flex-col mt-auto  !rounded-4xl h-full p-4"
                            style={{
                                backgroundColor: '#141414',
                                backgroundImage:
                                    item.pnlNumber > 0
                                        ? `url(${ShareWindImage})`
                                        : `url(${ShareLossImage})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat',
                            }}
                        >
                            <div className="py-4 flex justify-between items-center">
                                <div className="bg-white/10 rounded-full text-white text-xs w-fit px-2 pl-1 py-[2px] flex gap-x-1">
                                    {profile_picture_url && (
                                        <img
                                            src={profile_picture_url}
                                            className="h-4 w-4"
                                        />
                                    )}

                                    <p>{renderShortAddress(address)}</p>
                                </div>

                                <div className="flex-1">
                                    <img
                                        src={LogoApp}
                                        className="w-5 h-5 ml-auto"
                                    />
                                </div>
                                <p className="text-xs font-medium text-white ml-2">
                                    Winance
                                </p>
                            </div>

                            <div className="mt-8 flex items-center gap-x-2">
                                <img src={item.logoUrl} className="w-6 h-6" />
                                <span className="text-white">
                                    {item.nameDisplay}
                                </span>

                                <div
                                    className={clsx(
                                        'px-2 py-[3px] rounded-full text-xs',
                                        {
                                            'text-green1 bg-green1/10 border border-green-700':
                                                item.buy,
                                            'text-red1 bg-red1/10 border border-red-700':
                                                !item.buy,
                                        }
                                    )}
                                >
                                    <span>
                                        {item.buy ? 'Long' : 'Short'}{' '}
                                        {item.leverage}x
                                    </span>
                                </div>
                            </div>

                            <div
                                className={clsx(
                                    'px-2 py-[3px] text-[28px] text-black w-fit mt-4',
                                    {
                                        'bg-green1': item.pnlNumber >= 0,
                                        'bg-red1': item.pnlNumber < 0,
                                    }
                                )}
                            >
                                {item.pnlNumber >= 0 ? '+' : ''}
                                {formatDisplay(item.pnlPercent, {
                                    decimalToShow: 2,
                                })}
                                %
                            </div>

                            <div className="flex items-center justify-between mt-4 mb-2">
                                <div>
                                    <p className="text-white opacity-60 text-[10px]">
                                        {t('Entry Price')}
                                    </p>
                                    <p className="text-xs text-white">
                                        {formatDisplay(
                                            Number(item.openPrice) /
                                                Math.pow(10, 10),
                                            {
                                                decimalToShow:
                                                    item.decimalToShow,
                                            }
                                        )}{' '}
                                    </p>
                                </div>

                                <div>
                                    <p className="text-white opacity-60 text-[10px]">
                                        {t('Entry Price')}
                                    </p>
                                    <p className="text-xs text-white">
                                        {formatDisplay(
                                            Number(item.closePrice) /
                                                Math.pow(10, 10),
                                            {
                                                decimalToShow:
                                                    item.decimalToShow,
                                            }
                                        )}{' '}
                                    </p>
                                </div>

                                <div>
                                    <p className="text-white opacity-60 text-[10px]">
                                        {t('Invite Code')}
                                    </p>
                                    <p className="text-xs text-white">
                                        {data?.refCode}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="flex mt-4 justify-between gap-x-2">
                            <button
                                onClick={handleShare}
                                className="bg-[#1F1F1F] flex-1 py-4 flex justify-center font-medium text-white text-center  rounded-full"
                            >
                                {isSharing ? (
                                    <SpinLoading className="!w-4 !h-4" />
                                ) : (
                                    t('Share')
                                )}
                            </button>
                        </div>
                    </div>
                </Sheet.Content>
            </Sheet.Container>
        </Sheet>
    );
}
